/**
 * RequireAuth
 *
 * Route guard component that protects routes behind authentication
 * and (optionally) a required role.
 *
 * - While the auth check is loading, renders a centered spinner.
 * - If unauthenticated, redirects to /login.
 * - If a `role` is specified and the user does not hold that role
 *   (and is not a platform_admin), renders a 403 message.
 * - Otherwise renders children.
 */

import React, { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { useAuth } from './AuthProvider';

interface RequireAuthProps {
  /** If set, the user must have this role (or be platform_admin) to proceed. */
  role?: string;
  children: ReactNode;
}

export default function RequireAuth({ role, children }: RequireAuthProps) {
  const { user, isLoading, isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        role="status"
        aria-label="Checking authentication"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (role && user?.role !== role && !isAdmin) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={1}
      >
        <Typography variant="h4">403</Typography>
        <Typography variant="body1" color="text.secondary">
          You do not have permission to access this page.
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
}
